import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { guides } from '../data/guides';
import CodeBlock from '../components/CodeBlock';

// Slugify text into an HTML id
function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

// Extract H2 headings from markdown for the sidebar TOC
function extractHeadings(markdown) {
  const lines = markdown.split('\n');
  const headings = [];
  for (const line of lines) {
    const m2 = line.match(/^## (.+)/);
    if (m2) {
      const text = m2[1].trim().replace(/\*\*/g, '').replace(/`/g, '');
      headings.push({ text, id: slugify(text) });
    }
  }
  return headings;
}

// Strip the embedded TOC at the top of the markdown (before the first <hr>)
// but keep the H1 title. Also clean red section-label styles.
function preprocessMarkdown(md) {
  const lines = md.split('\n');
  // Find the first <hr line
  const firstHRIndex = lines.findIndex(l => l.trim().startsWith('<hr'));

  let result;
  if (firstHRIndex > 1) {
    const h1Line = lines.find(l => l.startsWith('# '));
    const rest = lines.slice(firstHRIndex + 1).join('\n');
    result = (h1Line ? h1Line + '\n\n' : '') + rest;
  } else {
    result = md;
  }

  // Remove red color from section labels like:
  // ### <p style="font-size: 24px; font-style: italic; color:red">TEXT</p>
  result = result.replace(
    /(<p[^>]*style=["'][^"']*?)color\s*:\s*red([^"']*["'][^>]*>)/gi,
    '$1color: #1a3d30$2'
  );

  return result;
}

const GuideDetailPage = () => {
  const { slug } = useParams();
  const guide = guides.find(g => g.slug === slug);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');
  const articleRef = useRef(null);
  // Refs for direct DOM updates — avoids re-render on every scroll tick
  const progressFillRef = useRef(null);
  const progressLabelRef = useRef(null);

  const currentIndex = guides.findIndex(g => g.slug === slug);
  const prevGuide = currentIndex > 0 ? guides[currentIndex - 1] : null;
  const nextGuide = currentIndex < guides.length - 1 ? guides[currentIndex + 1] : null;

  useEffect(() => {
    if (!guide) return;
    setLoading(true);
    setContent('');
    setHeadings([]);
    fetch(guide.contentFile)
      .then(r => r.text())
      .then(text => {
        const processed = preprocessMarkdown(text);
        setContent(processed);
        setHeadings(extractHeadings(processed));
        setLoading(false);
      })
      .catch(() => {
        setContent('# Content unavailable\nPlease check back later.');
        setLoading(false);
      });
  }, [guide]);

  // Reading progress — direct DOM update, no React re-render on scroll
  useEffect(() => {
    const handleScroll = () => {
      const el = articleRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const pct = Math.min(100, total > 0 ? (scrolled / total) * 100 : 0);
      if (progressFillRef.current)
        progressFillRef.current.style.width = `${pct}%`;
      if (progressLabelRef.current)
        progressLabelRef.current.textContent = `${Math.round(pct)}% read`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Active heading tracker - scroll-based for reliability
  useEffect(() => {
    if (!headings.length) return;
    
    const updateActiveHeading = () => {
      const scrollPosition = window.scrollY + 120; // offset for header
      
      // Find the current heading based on scroll position
      let current = headings[0].id;
      
      for (const heading of headings) {
        const element = document.getElementById(heading.id);
        if (element && element.offsetTop <= scrollPosition) {
          current = heading.id;
        } else {
          break;
        }
      }
      
      setActiveId(current);
    };
    
    // Initial check
    updateActiveHeading();
    
    // Update on scroll
    window.addEventListener('scroll', updateActiveHeading, { passive: true });
    return () => window.removeEventListener('scroll', updateActiveHeading);
  }, [headings]);

  // Scroll to top on slug change
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const scrollToHeading = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 72; // blog topbar height + some padding
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  if (!guide) return (
    <div className="guide-not-found">
      <h2>Guide not found</h2>
      <Link to="/guides">← Back to Guides</Link>
    </div>
  );

  // ReactMarkdown components — using pre to handle block code (fixes v10 inline detection)
  const mdComponents = {
    // Block code: handled in pre to avoid inline code being caught by code renderer
    pre: ({ children }) => {
      const child = React.Children.toArray(children).find(
        c => React.isValidElement(c) && c.type === 'code'
      );
      if (child) {
        const { className = '', children: codeContent } = child.props;
        const lang = className.replace('language-', '') || 'text';
        return <CodeBlock code={String(codeContent).replace(/\n$/, '')} lang={lang} />;
      }
      return <pre>{children}</pre>;
    },
    // Inline code only — block code is captured by pre above
    code: ({ children }) => (
      <code className="inline-code">{children}</code>
    ),
    // Paragraph: detect if it only contains an image, render figure directly
    p: ({ children, node }) => {
      const childArray = React.Children.toArray(children);
      // If paragraph only contains an img element, render it without <p> wrapper
      if (childArray.length === 1 && React.isValidElement(childArray[0])) {
        if (childArray[0].type === 'img' || childArray[0].props?.node?.tagName === 'img') {
          return <>{children}</>;
        }
      }
      return <p>{children}</p>;
    },
    img: ({ src, alt }) => {
      // Resolve relative image paths (e.g. Resources/... or ./Resources/...) to /content/
      const resolvedSrc = src && !src.startsWith('http') && !src.startsWith('/')
        ? `/content/${src.replace(/^\.\//, '')}`
        : src;
      return (
      <figure className="guide-figure">
        <img src={resolvedSrc} alt={alt || ''} loading="lazy" />
        {alt && <figcaption>{alt}</figcaption>}
      </figure>
      );
    },
    table: ({ children }) => (
      <div className="guide-table-wrap"><table>{children}</table></div>
    ),
    blockquote: ({ children }) => (
      <blockquote className="guide-blockquote">{children}</blockquote>
    ),
    h1: ({ children }) => <h1 className="guide-h1">{children}</h1>,
    h2: ({ children }) => {
      const text = React.Children.toArray(children)
        .map(c => (typeof c === 'string' ? c : ''))
        .join('');
      const id = slugify(text);
      return <h2 id={id} className="guide-h2">{children}</h2>;
    },
    h3: ({ children }) => <h3 className="guide-h3">{children}</h3>,
    a: ({ href, children }) => (
      <a href={href} className="guide-link" target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
        {children}
      </a>
    ),
  };

  return (
    <div className="guide-detail">
      {/* Blog topbar */}
      <header className="blog-topbar">
        <div className="blog-topbar-left">
          <Link to="/guides" className="blog-topbar-back">← Blogs</Link>
          <Link to="/" className="blog-topbar-logo">SweetCodey</Link>
          <span className="blog-topbar-sep">/</span>
          <span className="blog-topbar-title">{guide.title}</span>
        </div>
        <div className="blog-topbar-right">
          <div className="blog-progress-wrap">
            <span className="blog-progress-label" ref={progressLabelRef}>0% read</span>
            <div className="blog-progress-bar">
              <div className="blog-progress-fill" ref={progressFillRef} style={{ width: '0%' }} />
            </div>
          </div>
          <Link to="/course" className="blog-access-btn">Get Access</Link>
        </div>
      </header>

      {/* Body layout */}
      <div className="guide-detail-body">
        {/* Permanent TOC sidebar */}
        <aside className="guide-toc-sidebar">
          <div className="guide-toc-header">
            <span className="guide-toc-header-label">IN THIS BLOG</span>
          </div>
          <nav className="guide-toc-nav-list">
            {headings.length === 0 && !loading && (
              <p className="guide-toc-empty">Loading sections…</p>
            )}
            {headings.map((h, i) => {
              const checkOverflow = (el) => {
                if (!el) return;
                const textSpan = el.querySelector('.guide-toc-text');
                if (textSpan && textSpan.scrollWidth > textSpan.clientWidth) {
                  el.setAttribute('title', h.text);
                } else {
                  el.removeAttribute('title');
                }
              };
              
              return (
                <button
                  key={i}
                  ref={checkOverflow}
                  className={`guide-toc-item-btn ${activeId === h.id ? 'active' : ''}`}
                  onClick={() => scrollToHeading(h.id)}
                >
                  <span className="guide-toc-dot" />
                  <span className="guide-toc-text">{h.text}</span>
                </button>
              );
            })}
          </nav>

          {/* Guide navigation at bottom of sidebar */}
          <div className="guide-sidebar-nav">
            {prevGuide && (
              <Link to={`/guides/${prevGuide.slug}`} className="guide-sidebar-nav-btn">
                <span className="guide-sidebar-nav-dir">← Previous</span>
                <span className="guide-sidebar-nav-name">{prevGuide.emoji} {prevGuide.title}</span>
              </Link>
            )}
            {nextGuide && (
              <Link to={`/guides/${nextGuide.slug}`} className="guide-sidebar-nav-btn next">
                <span className="guide-sidebar-nav-dir">Next →</span>
                <span className="guide-sidebar-nav-name">{nextGuide.emoji} {nextGuide.title}</span>
              </Link>
            )}
          </div>
        </aside>

        {/* Main article */}
        <main className="guide-content" ref={articleRef}>
          {loading ? (
            <div className="guide-loading">
              <div className="guide-loading-spinner" />
              <p>Loading guide…</p>
            </div>
          ) : (
            <article className="guide-markdown">
              {/* Clean article header */}
              <div className="blog-article-header">
                <span className="blog-article-emoji">{guide.emoji}</span>
                <h1 className="blog-article-title">{guide.title}</h1>
                <div className="blog-article-meta">
                  <span className="blog-article-badge">⏱ {guide.readTime} read</span>
                  <span className="blog-article-badge">📊 {guide.difficulty}</span>
                  {guide.tags.map(t => (
                    <span key={t} className="blog-article-tag">{t}</span>
                  ))}
                </div>
              </div>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={mdComponents}
              >
                {content}
              </ReactMarkdown>
            </article>
          )}

          {/* Bottom prev/next */}
          <div className="guide-bottom-nav">
            {prevGuide && (
              <Link to={`/guides/${prevGuide.slug}`} className="guide-nav-card prev">
                <span className="guide-nav-label">← Previous</span>
                <span className="guide-nav-name">{prevGuide.emoji} {prevGuide.title}</span>
              </Link>
            )}
            {nextGuide && (
              <Link to={`/guides/${nextGuide.slug}`} className="guide-nav-card next">
                <span className="guide-nav-label">Next →</span>
                <span className="guide-nav-name">{nextGuide.emoji} {nextGuide.title}</span>
              </Link>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default GuideDetailPage;
